import { Show } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { ApiProvider, useApi } from "./api-context";
import {
  canStartSelfServeWorkspace,
  queryAccountBilling,
} from "./account-billing";
import { AppSelect } from "./components/AppSelect";
import { DesktopUpdateButton } from "./components/DesktopUpdateButton";
import { BillingResumeScreen } from "./components/BillingResumeScreen";
import { BillingWorkspaceBanner } from "./components/BillingWorkspaceBanner";
import { HeaderConnectionNudge } from "./components/HeaderConnectionNudge";
import { HeaderQuickActions } from "./components/HeaderQuickActions";
import { ThemeToggle } from "./components/ThemeToggle";
import { WorkspaceRail } from "./components/WorkspaceRail";
import { isDesktopRuntime } from "./desktop";
import { IdentityBoot } from "./identity-boot";
import { afterAuthRedirect, signedOutAuthDestination } from "./self-serve-plan";
import {
  foreignOrganizationRedirect,
  workspaceEntryPath,
  workspaceSwitcherNeedsMenu,
  workspaceSwitcherOrganizations,
} from "./workspace-switcher";

const KnowledgePage = lazy(() => import("./pages/KnowledgePage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const ConflictsPage = lazy(() => import("./pages/ConflictsPage"));
const ConnectionsPage = lazy(() => import("./pages/ConnectionsPage"));
const AuditPage = lazy(() => import("./pages/AuditPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const SkillsPage = lazy(() => import("./pages/SkillsPage"));
const SkillEditorPage = lazy(() => import("./pages/SkillEditorPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const TemplateEditorPage = lazy(() => import("./pages/TemplateEditorPage"));
const CreateWorkspacePage = lazy(() => import("./pages/CreateWorkspacePage"));
const BillingCompletePage = lazy(() => import("./pages/BillingCompletePage"));
const InvitationPage = lazy(() => import("./pages/InvitationPage"));
const PublicSharePage = lazy(() => import("./pages/PublicSharePage"));
const SignInPage = lazy(() => import("./pages/SignInPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const FirstRunSetupPage = lazy(() => import("./pages/FirstRunSetupPage"));

function PageFallback() {
  return (
    <div className="page-state" role="status">
      <span className="working-mark" aria-hidden="true" />
      <p>Opening this workspace…</p>
    </div>
  );
}

export function ConfigurationRequired() {
  return (
    <main className="entry-screen">
      <ThemeToggle variant="entry" />
      <div className="entry-index" aria-hidden="true">
        NL / AUTH
      </div>
      <section className="entry-card" aria-labelledby="config-title">
        <p className="utility-label">Configuration required</p>
        <h1 id="config-title">Connect the identity boundary.</h1>
        <p>
          Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in the web environment,
          and set <code>VITE_API_BASE_URL</code> for a packaged desktop build,
          then restart the application. The API remains unavailable until Clerk
          can issue a verified session against the configured Worker origin.
        </p>
      </section>
    </main>
  );
}

function SignedOutRedirect() {
  const location = useLocation();
  const to = signedOutAuthDestination(location.pathname, location.search);
  if (to === null) return <PageFallback />;
  return <Navigate to={to} replace />;
}

function AuthRoute({
  signedInTo,
  children,
}: {
  readonly signedInTo: string;
  readonly children: ReactNode;
}) {
  return (
    <Show when="signed-out" fallback={<Navigate to={signedInTo} replace />}>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </Show>
  );
}

function SignInRoute() {
  const [params] = useSearchParams();
  return (
    <AuthRoute signedInTo={afterAuthRedirect(params)}>
      <SignInPage />
    </AuthRoute>
  );
}

function SignUpRoute() {
  const [params] = useSearchParams();
  return (
    <AuthRoute signedInTo={afterAuthRedirect(params)}>
      <SignUpPage />
    </AuthRoute>
  );
}

function OrganizationHome() {
  const destination = useWorkspaceEntry("knowledge");
  if (destination.status === "loading") return <PageFallback />;
  if (destination.status === "error") {
    return <ErrorState error={destination.error} retry={destination.retry} />;
  }
  if (destination.status === "none") {
    return (
      <Suspense fallback={<PageFallback />}>
        <CreateWorkspacePage />
      </Suspense>
    );
  }
  return <Navigate to={destination.to} replace />;
}

function AdminRedirect() {
  const destination = useWorkspaceEntry("admin");
  if (destination.status === "loading") return <PageFallback />;
  if (destination.status === "error") {
    return <ErrorState error={destination.error} retry={destination.retry} />;
  }
  if (destination.status === "none") return <Navigate to="/" replace />;
  return <Navigate to={destination.to} replace />;
}

function useWorkspaceEntry(
  section: "knowledge" | "admin",
):
  | { status: "loading" }
  | { status: "error"; error: unknown; retry: () => unknown }
  | { status: "none" }
  | { status: "ready"; to: string } {
  const api = useApi();
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
  });
  const operator = useQuery({
    queryKey: ["operator-organizations"],
    queryFn: () => api.operatorOrganizations(),
    retry: false,
  });

  if (organizations.isPending) return { status: "loading" };
  if (organizations.isError) {
    return {
      status: "error",
      error: organizations.error,
      retry: organizations.refetch,
    };
  }
  if (organizations.data.organizations.length === 0 && operator.isPending) {
    return { status: "loading" };
  }
  const to = workspaceEntryPath({
    section,
    memberOrganizations: organizations.data.organizations,
    isOperator: operator.isSuccess,
  });
  if (to === null) return { status: "none" };
  return { status: "ready", to };
}

export function ErrorState({
  error,
  retry,
}: {
  readonly error: unknown;
  readonly retry?: () => unknown;
}) {
  const message =
    error instanceof Error
      ? error.message.replaceAll("_", " ")
      : "unknown error";
  return (
    <section className="page-state page-state--error" role="alert">
      <p className="utility-label">Request stopped</p>
      <h2>{message}</h2>
      <p>Check your access and try the request again.</p>
      {retry === undefined ? null : (
        <button
          className="secondary-action"
          type="button"
          onClick={() => retry()}
        >
          Try again
        </button>
      )}
    </section>
  );
}

function WorkspaceLayout() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId = "" } = useParams();
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
  });
  const operator = useQuery({
    queryKey: ["operator-organizations"],
    queryFn: () => api.operatorOrganizations(),
    retry: false,
  });
  const memberPreview = organizations.data?.organizations.find(
    (organization) => organization.organization_id === organizationId,
  );
  const billingUsage = useQuery({
    queryKey: ["billing-usage", memberPreview?.billing_account_id],
    queryFn: () => api.billingUsage(memberPreview?.billing_account_id ?? ""),
    enabled: queryAccountBilling({
      billingAccountId: memberPreview?.billing_account_id ?? null,
      plan: memberPreview?.plan ?? null,
      organizationRole: memberPreview?.role,
    }),
    retry: false,
  });

  if (organizations.isPending) return <PageFallback />;
  if (organizations.isError) {
    return (
      <ErrorState error={organizations.error} retry={organizations.refetch} />
    );
  }
  if (organizationId.length === 0 && operator.isPending) {
    return <PageFallback />;
  }

  const member = organizations.data.organizations.find(
    (organization) => organization.organization_id === organizationId,
  );
  const isOperator = operator.isSuccess;
  const isMember = member !== undefined;
  const canAdminister =
    member?.role === "org_owner" || member?.role === "org_admin";
  const workspacePaused = isMember && member.lifecycle_state === "suspended";
  const showAdmin = canAdminister || isOperator;
  const showTemplates = organizationId.length > 0 && isMember;
  const switcherOrganizations = workspaceSwitcherOrganizations(
    organizations.data.organizations,
  );
  const active = switcherOrganizations.find(
    (organization) => organization.organization_id === organizationId,
  );
  const section =
    organizationId.length > 0
      ? (location.pathname.split("/")[3] ?? "knowledge")
      : location.pathname.includes("/templates/")
        ? "templates"
        : location.pathname.includes("/skills/")
          ? "skills"
          : "admin";

  if (organizationId.length > 0 && active === undefined) {
    if (operator.isPending) return <PageFallback />;
    return (
      <Navigate to={foreignOrganizationRedirect({ isOperator })} replace />
    );
  }
  const firstSwitcher = switcherOrganizations[0];
  if (
    organizationId.length === 0 &&
    firstSwitcher !== undefined &&
    section !== "templates" &&
    section !== "skills"
  ) {
    return (
      <Navigate to={`/org/${firstSwitcher.organization_id}/admin`} replace />
    );
  }
  if (organizationId.length === 0 && !isOperator) {
    return <Navigate to="/" replace />;
  }
  if (
    organizationId.length > 0 &&
    isMember &&
    !canAdminister &&
    !isOperator &&
    section === "admin"
  ) {
    return <Navigate to={`/org/${organizationId}/knowledge`} replace />;
  }

  return (
    <div className="product-shell">
      <WorkspaceRail
        organizationId={organizationId}
        showWorkspaceNav={isMember}
        showTemplates={showTemplates}
        showAdmin={showAdmin}
        billingAccountId={member?.billing_account_id ?? null}
        plan={member?.plan ?? null}
        organizationRole={member?.role}
      />

      <div className="product-main">
        <header className="product-header">
          {switcherOrganizations.length === 0 ? (
            <div className="organization-control">
              <label htmlFor="organization-empty">Workspace</label>
              <span id="organization-empty">None provisioned</span>
            </div>
          ) : (
            <div className="organization-control">
              <label htmlFor="organization-switcher">Workspace</label>
              {workspaceSwitcherNeedsMenu(switcherOrganizations.length) ? (
                <AppSelect
                  id="organization-switcher"
                  aria-label="Workspace"
                  value={organizationId}
                  placeholder="Select workspace"
                  options={switcherOrganizations.map((organization) => ({
                    value: organization.organization_id,
                    label: organization.name,
                  }))}
                  onChange={(nextOrganizationId) => {
                    void navigate(`/org/${nextOrganizationId}/${section}`);
                  }}
                />
              ) : (
                <span id="organization-switcher">
                  {active?.name ?? "Workspace"}
                </span>
              )}
              {canStartSelfServeWorkspace(organizations.data.organizations) ? (
                <Link className="text-action" to="/create">
                  Create workspace
                </Link>
              ) : null}
            </div>
          )}
          <div className="product-header__tools">
            {isDesktopRuntime() ? <DesktopUpdateButton /> : null}
            {isMember && organizationId.length > 0 && !workspacePaused ? (
              <>
                <HeaderConnectionNudge organizationId={organizationId} />
                <HeaderQuickActions
                  organizationId={organizationId}
                  canAdminister={canAdminister}
                />
              </>
            ) : null}
          </div>
        </header>

        {workspacePaused || billingUsage.data === undefined ? null : (
          <BillingWorkspaceBanner
            usage={billingUsage.data}
            organizationId={organizationId}
            organizationRole={member?.role}
          />
        )}

        <Suspense fallback={<PageFallback />}>
          {workspacePaused ? (
            <BillingResumeScreen
              organizationId={organizationId}
              billingAccountId={member.billing_account_id}
              canResume={canAdminister}
            />
          ) : (
            <Outlet />
          )}
        </Suspense>
      </div>
    </div>
  );
}

function AuthenticatedRoutes() {
  return (
    <ApiProvider>
      <Routes>
        <Route path="/" element={<OrganizationHome />} />
        <Route
          path="/create"
          element={
            <Suspense fallback={<PageFallback />}>
              <CreateWorkspacePage />
            </Suspense>
          }
        />
        <Route
          path="/billing/complete"
          element={
            <Suspense fallback={<PageFallback />}>
              <BillingCompletePage />
            </Suspense>
          }
        />
        <Route
          path="/invite"
          element={
            <Suspense fallback={<PageFallback />}>
              <InvitationPage />
            </Suspense>
          }
        />
        <Route path="/org/:organizationId" element={<WorkspaceLayout />}>
          <Route index element={<Navigate to="knowledge" replace />} />
          <Route
            path="knowledge/:vaultId?/:fileId?"
            element={<KnowledgePage />}
          />
          <Route path="setup" element={<FirstRunSetupPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="conflicts/:conflictId?" element={<ConflictsPage />} />
          <Route path="connections" element={<ConnectionsPage />} />
          <Route path="audit/:cursor?" element={<AuditPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route
            path="templates/:templateId/:entryId?"
            element={<TemplateEditorPage />}
          />
          <Route path="skills" element={<SkillsPage />} />
          <Route
            path="skills/:skillId/:entryId?"
            element={<SkillEditorPage />}
          />
          <Route path="admin" element={<AdminPage />} />
        </Route>
        <Route path="/admin" element={<WorkspaceLayout />}>
          <Route index element={<AdminPage />} />
          <Route
            path="templates/:templateId/:entryId?"
            element={<TemplateEditorPage />}
          />
        </Route>
        <Route path="/operator" element={<AdminRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ApiProvider>
  );
}

export function App() {
  return (
    <IdentityBoot>
      <Routes>
        <Route
          path="/s/:token"
          element={
            <ApiProvider>
              <Suspense fallback={<PageFallback />}>
                <PublicSharePage />
              </Suspense>
            </ApiProvider>
          }
        />
        <Route path="/sign-in/*" element={<SignInRoute />} />
        <Route path="/sign-up/*" element={<SignUpRoute />} />
        <Route
          path="*"
          element={
            <Show when="signed-in" fallback={<SignedOutRedirect />}>
              <AuthenticatedRoutes />
            </Show>
          }
        />
      </Routes>
    </IdentityBoot>
  );
}
